import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/ui/Layout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { supabase } from '@/lib/supabase';
import { Loader2, Download, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ResponseSummary } from '@/components/surveys/ResponseSummary';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface SurveyMetrics {
  total_invites: number;
  pending_invites: number;
  total_responses: number;
  completion_rate: number;
  total_views: number;
}

interface ResponseData {
  question: string;
  type: 'multiple-choice' | 'open-ended' | 'multi-select';
  responses: {
    answer: string;
    count: number;
  }[];
  responseCount?: number;
  questionId: string;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export function SurveyReport() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [metrics, setMetrics] = useState<SurveyMetrics | null>(null);
  const [responseData, setResponseData] = useState<ResponseData[]>([]);
  const [surveyTitle, setSurveyTitle] = useState('');
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    if (surveyId) {
      loadSurveyData();
    }
  }, [surveyId, timeRange]);

  const loadSurveyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get survey template details
      const { data: template, error: templateError } = await supabase
        .from('survey_templates')
        .select(`
          *,
          questions:survey_questions(*)
        `)
        .eq('id', surveyId)
        .single();

      if (templateError) {
        throw new Error(`Failed to load survey template: ${templateError.message}`, { cause: templateError });
      }

      setSurveyTitle(template.title);

      // Get metrics
      const days = timeRange === 'week' ? 7 : timeRange === 'year' ? 365 : 30;
      const { data: metricsData, error: metricsError } = await supabase.rpc(
        'get_survey_metrics',
        { survey_id: surveyId, days }
      );

      if (metricsError) {
        throw new Error(`Failed to load survey metrics: ${metricsError.message}`, { cause: metricsError });
      }
      
      // Ensure completion rate is capped at 100%
      if (metricsData) {
        metricsData.completion_rate = Math.min(metricsData.completion_rate, 100);
      }
      
      setMetrics(metricsData);

      // Get response data for each question
      const responseDataPromises = template.questions.map(async (question: any) => {
        if (question.type === 'multiple-choice' || question.type === 'multi-select') {
          const { data: responses, error: responsesError } = await supabase
            .from('survey_responses')
            .select('answer')
            .eq('question_id', question.id);

          if (responsesError) {
            throw new Error(`Failed to load responses: ${responsesError.message}`, { cause: responsesError });
          }

          const counts = responses?.reduce((acc: Record<string, number>, curr) => {
            if (question.type === 'multi-select') {
              let options: string[];
              try {
                options = JSON.parse(curr.answer);
              } catch {
                options = curr.answer.includes(', ') ? curr.answer.split(', ') : [curr.answer];
              }
              options.forEach((opt: string) => {
                const trimmed = opt.trim();
                if (trimmed) acc[trimmed] = (acc[trimmed] || 0) + 1;
              });
            } else {
              acc[curr.answer] = (acc[curr.answer] || 0) + 1;
            }
            return acc;
          }, {});

          return {
            question: question.text,
            type: question.type,
            responses: Object.entries(counts || {}).map(([answer, count]) => ({
              answer,
              count
            })),
            questionId: question.id
          };
        } else {
          const { count } = await supabase
            .from('survey_responses')
            .select('*', { count: 'exact', head: true })
            .eq('question_id', question.id)
            .not('answer', 'eq', '');

          return {
            question: question.text,
            type: question.type,
            responses: [],
            responseCount: count || 0,
            questionId: question.id
          };
        }
      });

      const responseData = await Promise.all(responseDataPromises);
      setResponseData(responseData);

    } catch (err) {
      console.error('Error loading survey data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load survey data'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Back Link */}
        <div className="flex items-center space-x-3">
          <Link 
            to="/reports"
            className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="text-sm">Back to Reports</span>
          </Link>
        </div>

        {error && (
          <ErrorDisplay 
            error={error}
            context="Survey Report"
            onRetry={loadSurveyData}
          />
        )}

        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Survey Report</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {surveyTitle}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="block rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
              <option value="year">Last 12 months</option>
            </select>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Metrics Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Invitations</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {metrics?.total_invites || 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Invitations</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {metrics?.pending_invites || 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Responses</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {metrics?.total_responses || 0}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Completion Rate</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {metrics?.completion_rate || 0}%
            </p>
          </div>
        </div>

        {/* Response Analysis */}
        <div className="space-y-6">
          {responseData.map((data, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {data.question}
                </h2>
              </div>
              <div className="p-6">
                {(data.type === 'multiple-choice' || data.type === 'multi-select') ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.responses}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="answer" 
                          stroke="#9CA3AF"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                        />
                        <YAxis stroke="#9CA3AF" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1F2937',
                            border: 'none',
                            borderRadius: '0.375rem',
                            color: '#F3F4F6'
                          }}
                        />
                        <Bar dataKey="count" fill="#3B82F6">
                          {data.responses.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <ResponseSummary
                    surveyId={surveyId}
                    questionId={data.questionId}
                    questionText={data.question}
                    responseCount={data.responseCount || 0}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}